import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { PageTitle } from '@/components/ui/text';
import { ProjectHeader } from '@/components/project/project-header';
import { ProjectOverview } from '@/components/project/project-overview';
import { ProjectStories } from '@/components/project/project-stories';
import { ProjectTabs } from '@/components/project/project-tabs';
import { Story } from '@/types/cmsTypes';
import { useOrganization } from '@/hooks/useOrganization';
import { usePlanState } from '@/modules/subscriptionPlan/usePlanState';
import { useAuthUser } from '@/hooks/useAuthUser';
import { invalidateShareLinks } from '@/hooks/useShareLink';
import { ProjectMapRef } from '@/components/project/project-map';
import { useServices } from '@/infrastructure/providers/ServiceProvider';
import { toast } from 'sonner';
import { ProjectPublishFlowModal } from '@/components/publish-flow/ProjectPublishFlowModal';
import { ProjectShareDialog } from '@/components/publish-flow/ProjectShareDialog';
import type { ProjectPublishSubmitArgs, ProjectSceneTarget } from '@/components/publish-flow/types';
import { PrePublishCheckDialog } from '@/components/dialogs/pre-publish-check-dialog';
import { ProjectCopyDialog } from '@/components/dialogs/project-copy-dialog';
import { StoriesLimitWarningDialog } from '@/components/dialogs/stories-limit-warning-dialog';
import { DeleteDialog } from '@/components/dialogs/scene-project-delete-dialog';
import { shareLinkUrl } from '@/modules/databaseService/scenes/shareLinks';

import type { DragEndEvent } from '@dnd-kit/core';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { useCmsProjectTour, CmsProjectTour } from '@/modules/cmsOnboarding';
import { v1, toResult } from '@/infrastructure/api/v1/client';
import { storyReorder, REORDER_PAST_LIMIT_MESSAGE } from '@/utils/storyReorder';
import { PageFrame } from '@/components/ui/page-frame';
import { ProjectDetailSkeleton } from './projectDetail/ProjectDetailSkeleton';
import { useProjectPageData } from './projectDetail/useProjectPageData';
import { highestStatus } from './projectDetail/loadProjectPage';
import { buildProjectSceneTargets } from './projectDetail/projectSceneTargets';
import { submitProjectForReview } from './projectDetail/projectPublishSubmit';
import { useNewStory } from './projectDetail/useNewStory';
import { copyProjectToUsers } from './projectDetail/copyProjectToUsers';
import { projectLocation } from './projectDetail/projectLocation';

interface StoryToDelete {
  id: string;
  title: string;
  type: 'project' | 'scene';
  isLive: boolean;
  liveLinkUrl: string | null;
  liveLinkUnknown: boolean;
}

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const publishHandoffRef = useRef(false);
  const { user, loading: authLoading } = useAuthUser();
  const userId = user?.id;
  const services = useServices();
  const projectMapRef = useRef<ProjectMapRef>(null);
  const [selectedStoryId, setSelectedStoryId] = useState<string | undefined>();
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const {
    loadState, loadError, project, setProject, stories, setStories, storiesError,
    projectStatus, setProjectStatus, statusUnknown, heroImage, heroImageCrop, reload, localWrite,
  } = useProjectPageData(id, userId, authLoading);
  const projectId = project?.id;
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [sceneTargets, setSceneTargets] = useState<ProjectSceneTarget[]>([]);
  const [sceneIntroById, setSceneIntroById] = useState<Record<string, string>>({});
  const [targetsBusy, setTargetsBusy] = useState(false);
  const targetsBusyRef = useRef(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [prePublishCheckOpen, setPrePublishCheckOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<StoryToDelete | null>(null);
  const planState = usePlanState();
  const { orgMember } = useOrganization();
  const canCopyProject = planState.status === 'ready' && !!planState.plan && services.subscriptionPlan.canCopyProject(planState.plan);
  const { isCreatingStory, storiesLimitDialog, setStoriesLimitDialog, createBlankStory, confirmCreateStory } = useNewStory({
    userId, projectId, planState, services, navigate, reload,
  });

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // Latest project id for the share-dialog event, which fires only when the dialog opens
  const shareProjectIdRef = useRef(projectId);
  useLayoutEffect(() => {
    shareProjectIdRef.current = projectId;
  });
  useEffect(() => {
    if (!shareDialogOpen) return;
    services.analytics.trackLureoShareDialogOpened({
      project_instance_id: shareProjectIdRef.current,
      target: 'phone',
    });
  }, [shareDialogOpen, services.analytics]);
  const [totalSceneOrigins, setTotalSceneOrigins] = useState(0);

  // Enable scrolling for ProjectDetail
  useLayoutEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Fetch total scene origins count for onboarding tour (show when <= 1 story)
  useEffect(() => {
    const fetchSceneOriginsCount = async () => {
      if (!user?.id) return;
      try {
        // The tour only asks whether the user has more than one story, so two answer it
        const { data, error } = await toResult(v1.GET('/api/v1/stories', { params: { query: { userUniqueId: user.id, limit: 2 } } }));
        if (!error) setTotalSceneOrigins(data.items.length);
      } catch {
        setTotalSceneOrigins(0);
      }
    };
    fetchSceneOriginsCount();
  }, [user?.id]);

  const loading = loadState === 'loading';

  // Open the publish flow when arriving from the edit page's Publish button.
  // Mirrors handlePublishProject (the pre-publish check).
  const arrivedToPublish = !!(location.state as { openPublish?: boolean } | null)?.openPublish;
  const [publishHandoffDone, setPublishHandoffDone] = useState(false);
  if (!publishHandoffDone && arrivedToPublish && !loading && project) {
    setPublishHandoffDone(true);
    setPrePublishCheckOpen(true);
  }
  useEffect(() => {
    if (!publishHandoffDone || publishHandoffRef.current) return;
    publishHandoffRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });
  }, [publishHandoffDone, location.pathname, navigate]);

  // Memoized project format conversion to avoid recalculating on every render
  const projectForUI = useMemo(() => {
    if (!project) return null;
    return {
      id: project.id,
      title: project.projectName || 'Untitled Project',
      description: project.description || 'No description available',
      heroImage,
      heroImageCrop,
      duration: project.totalDuration ? Math.ceil(project.totalDuration / 60) : 0,
      tags: project.tags || [],
      status: projectStatus,
      locationInfo: projectLocation(project, stories),
      createdAt: project.createdAt ? new Date(project.createdAt) : new Date(),
      updatedAt: project.updatedAt ? new Date(project.updatedAt) : new Date(),
      storiesCount: project.totalScenes || 0,
      views: 0, // Not available in current schema
      completionRate: 0 // Not available in current schema
    };
  }, [project, projectStatus, stories, heroImage, heroImageCrop]);

  // CMS onboarding tour (must be before any conditional return to satisfy rules of hooks)
  const isFullyLoaded = !loading && project !== null;
  const { run: runProjectTour, steps: projectSteps, handleJoyrideCallback: handleProjectTourCallback } =
    useCmsProjectTour(isFullyLoaded, totalSceneOrigins);

  // Memoized and optimized event handlers (MUST be defined before conditional returns)
  const handleEditStory = useCallback((story: Story) => {
    if (project?.id) {
      navigate(`/story/edit/${story.id}`);
    }
  }, [navigate, project?.id]);

  const handleViewStory = useCallback((story: Story) => {
    setSelectedStoryId(story.id);
  }, []);

  const handleDeleteStory = useCallback(async (story: Story) => {
    if (!user?.id) {
      toast.error('You must be logged in to delete a story');
      return;
    }
    const isLive = story.preCalculatedStatus === 'live';
    let liveLinkUrl: string | null = null;
    let liveLinkUnknown = false;
    if (isLive) {
      try {
        const { live } = await services.scene.getShareLinks(story.id);
        liveLinkUrl = live ? shareLinkUrl(live.slug) : null;
      } catch (error) {
        console.error('Error reading the live link for the delete warning:', error);
        liveLinkUnknown = true;
      }
    }
    setItemToDelete({ id: story.id, title: story.title, type: 'scene', isLive, liveLinkUrl, liveLinkUnknown });
    setDeleteDialogOpen(true);
  }, [user?.id, services]);

  const handleAddStory = useCallback(async () => {
    if (!userId || !projectId || isCreatingStory) return;
    // Skip the device + template picker; create a blank scene with no device payload
    // and navigate straight to the editor (parity with the dashboard New Story flow).
    await createBlankStory();
  }, [userId, projectId, isCreatingStory, createBlankStory]);

  const handleEditProject = useCallback(() => {
    if (projectId) {
      navigate(`/project/edit/${projectId}`);
    }
  }, [navigate, projectId]);

  // Handle map image save callback from ProjectMap component
  const handleMapImageSaved = useCallback((imageUrl: string | null) => {
    if (imageUrl) {
      toast.success('Project map image saved');
    } else {
      toast.error('Failed to save map image');
    }
  }, []);

  // All conditional returns AFTER all hooks are declared
  if (loading || authLoading) {
    return <ProjectDetailSkeleton />;
  }

  // If auth is done loading but no user, redirect to login
  if (!authLoading && !user) {
    navigate('/login');
    return null;
  }

  if (loadState === 'error' || !project) {
    const failed = loadState === 'error';
    return (
      <PageFrame layout="centred">
        <Card padding="lg" className="text-center">
          <PageTitle as="h2" className="mb-4">{failed ? "We couldn't load this project" : 'Project Not Found'}</PageTitle>
          <p className="mb-6 text-lureo-ink-subtle">
            {failed ? (loadError?.message || 'Something went wrong. Please try again.') : "The project you're looking for doesn't exist."}
          </p>
          <div className="flex justify-center gap-3">
            <LureoButton
              variant="no-outline"
              onClick={() => navigate('/dashboard')}
              className="h-9 w-9 p-0 md:w-auto md:px-4">
              <ArrowLeftIcon className="h-5 w-5" />
              <span className="hidden md:inline">Dashboard</span>
            </LureoButton>
            {failed && (
              <LureoButton variant="secondary" onClick={() => void reload()} className="h-9 rounded-lureo-radius-button-pill px-4">
                Try again
              </LureoButton>
            )}
          </div>
        </Card>
      </PageFrame>
    );
  }

  const handleConfirmDeleteStory = async (item: { id: string; title: string; type: 'project' | 'scene' }) => {
    if (!user?.id || !project?.id) return;

    try {
      // The server renumbers the remaining stories and refreshes the project summary in the same transaction
      await localWrite(async () => {
        await services.scene.deleteSceneOriginRow(item.id);
        const remaining = stories.filter((s) => s.id !== item.id).map((story, index) => ({ ...story, order: index + 1 }));
        setStories(remaining);
        setProject((prev) => (prev ? { ...prev, totalScenes: Math.max(0, (prev.totalScenes || 0) - 1) } : null));
        if (!statusUnknown) setProjectStatus(highestStatus(remaining.map((s) => s.preCalculatedStatus ?? 'draft')));
      });
      invalidateShareLinks(item.id);
      toast.success('Story deleted successfully');
    } catch (error) {
      console.error('Error deleting story:', error);
      toast.error('Failed to delete story');
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id === over?.id) return;

    const oldIndex = stories.findIndex((item) => item.id === active.id);
    const newIndex = stories.findIndex((item) => item.id === over!.id);

    const reorderedItems = [...stories];
    const [reorderedItem] = reorderedItems.splice(oldIndex, 1);
    reorderedItems.splice(newIndex, 0, reorderedItem);

    // Only the ids up to the last moved position go out (at most 100); the rest keep their order
    const { storyIds, ordered, exact } = storyReorder(stories, reorderedItems);
    if (!exact) {
      toast.error(REORDER_PAST_LIMIT_MESSAGE);
      return;
    }
    const previous = stories;
    try {
      await localWrite(() => {
        setStories(ordered.map((item, index) => ({ ...item, order: index + 1 })));
        return services.scene.reorderStories(id!, storyIds);
      });
      toast.success('Story order updated');
    } catch (error) {
      console.error('Error updating story order:', error);
      setStories(previous);
      toast.error('Failed to update story order');
    }
  };

  const handlePublishReorder = async (orderedSceneOriginIds: string[]) => {
    const inSet = orderedSceneOriginIds
      .map((id) => stories.find((s) => s.id === id))
      .filter((s): s is Story => s != null);
    const rest = stories.filter((s) => !orderedSceneOriginIds.includes(s.id));
    // Only the ids up to the last moved position go out (at most 100); the rest keep their order
    const { storyIds, ordered, exact } = storyReorder(stories, [...inSet, ...rest]);
    if (!exact) {
      toast.error(REORDER_PAST_LIMIT_MESSAGE);
      return;
    }
    const previousStories = stories;
    const previousTargets = sceneTargets;

    // Stories are the caller's own; a target owned by someone else keeps its place after them
    const targetById = new Map(sceneTargets.map((t) => [t.sceneOriginId, t]));
    const orderedIds = new Set(ordered.map((s) => s.id));
    const newTargets = [
      ...ordered.map((s) => targetById.get(s.id)).filter((t): t is ProjectSceneTarget => t != null),
      ...sceneTargets.filter((t) => !orderedIds.has(t.sceneOriginId)),
    ].map((t, i) => ({ ...t, orderNumber: i + 1 }));

    if (storyIds.length === 0) {
      setStories(ordered.map((s, i) => ({ ...s, order: i + 1 })));
      setSceneTargets(newTargets);
      return;
    }
    try {
      await localWrite(() => {
        setStories(ordered.map((s, i) => ({ ...s, order: i + 1 })));
        setSceneTargets(newTargets);
        return services.scene.reorderStories(id!, storyIds);
      });
    } catch (error) {
      console.error('Error updating story order:', error);
      setStories(previousStories);
      setSceneTargets(previousTargets);
      toast.error('Failed to update story order');
    }
  };

  const handlePublishProject = () => {
    // Step zero: ask the user if they actually meant to publish, since many
    // creators reach for Publish when what they wanted was to test on mobile.
    setPrePublishCheckOpen(true);
  };

  // One build at a time; a failed read says so instead of opening a dialog with no stories
  const loadSceneTargets = async (): Promise<boolean> => {
    if (targetsBusyRef.current) return false;
    targetsBusyRef.current = true;
    setTargetsBusy(true);
    try {
      const { targets, introById } = await buildProjectSceneTargets(project.id, stories);
      setSceneTargets(targets);
      setSceneIntroById(introById);
      return true;
    } catch (error) {
      console.error("Error reading the project's stories:", error);
      toast.error("We couldn't load this project's stories. Try again.", { id: 'scene-targets-failed' });
      return false;
    } finally {
      targetsBusyRef.current = false;
      setTargetsBusy(false);
    }
  };

  // The share dialog lists the same stories, covers and versions as the publish flow
  const openShareDialog = async () => {
    if (await loadSceneTargets()) setShareDialogOpen(true);
  };

  const continuePublishProject = async () => {
    if (!(await loadSceneTargets())) return;
    setPublishDialogOpen(true);
  };

  const handleProjectPublishSubmit = async (args: ProjectPublishSubmitArgs) => {
    if (!project) throw new Error('No project to publish');
    const newStatus = await submitProjectForReview(services, { projectId: project.id, userId: user?.id, args });
    if (newStatus) setProjectStatus(newStatus);
    // Story-list refresh runs on modal close, not here: it would reset the open modal.

    toast.success(
      `Submitted ${args.scenes.length} ${args.scenes.length === 1 ? 'story' : 'stories'} for review. We'll let you know within 7 days.`,
    );
  };

  const handlePublishModalOpenChange = (next: boolean) => {
    setPublishDialogOpen(next);
    if (!next) void reload();
  };

  const handleCopyProject = () => {
    setCopyDialogOpen(true);
  };

  const handleCopyStories = async (targetEmails: string[], selectedSceneInstanceIds: string[], targetProjectName?: string) => {
    if (!user || !id || !canCopyProject) return { success: false };
    return copyProjectToUsers(id, targetEmails, selectedSceneInstanceIds, targetProjectName);
  };

  return (
    <PageFrame layout="content" className="select-none">
      <CmsProjectTour
        run={runProjectTour}
        steps={projectSteps}
        onCallback={handleProjectTourCallback}
      />
      <ProjectHeader
        title={projectForUI.title}
        status={projectForUI.status}
        onBack={() => navigate('/dashboard')}
        onCopy={handleCopyProject}
        onTest={openShareDialog}
        onEdit={handleEditProject}
        onPublish={handlePublishProject}
        canCopy={canCopyProject}
        busy={targetsBusy}
      />

      <div className="container py-6 md:py-8">
        <ProjectOverview
          project={projectForUI}
          storyCount={stories.length}
          coverSeed={id || ''}
          onChangeCover={() => navigate(`/project/edit/${id}`)}
        />

        <ProjectTabs>
          <ProjectStories
            stories={stories}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            isCreatingStory={isCreatingStory}
            onAddStory={handleAddStory}
            onDragEnd={handleDragEnd}
            onEditStory={handleEditStory}
            onDeleteStory={handleDeleteStory}
            onSelectStory={handleViewStory}
            selectedStoryId={selectedStoryId}
            plan={planState}
            onRefresh={reload}
            statusUnknown={statusUnknown}
            runWrite={localWrite}
            loadError={!!storiesError}
            onRetry={() => void reload()}
            mapRef={projectMapRef}
            onSaveMapImage={handleMapImageSaved}
            projectId={project?.id}
            userId={user?.id}
          />
        </ProjectTabs>
      </div>

      <ProjectShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        scenes={sceneTargets}
      />

      <PrePublishCheckDialog
        open={prePublishCheckOpen}
        onOpenChange={setPrePublishCheckOpen}
        onProceed={continuePublishProject}
        onShowTest={openShareDialog}
        noun="project"
        surface="projects_page"
        projectId={project.id}
      />

      <ProjectPublishFlowModal
        open={publishDialogOpen}
        onOpenChange={handlePublishModalOpenChange}
        projectInstanceId={project.id}
        scenes={sceneTargets}
        creatorName={orgMember?.fullName || project?.sourceName || user?.email || ''}
        initialProjectName={projectForUI.title}
        initialProjectDescription={project?.description || ''}
        initialProjectTags={project?.tags || []}
        initialSceneIntro={sceneIntroById}
        onReorderScenes={handlePublishReorder}
        onSubmit={handleProjectPublishSubmit}
      />

      {/* Project Copy Dialog */}
      {canCopyProject && (
      <ProjectCopyDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        onConfirm={handleCopyStories}
        onCancel={() => setCopyDialogOpen(false)}
        stories={stories}
        projectName={projectForUI.title}
        projectId={project.id}
      />
      )}


      {/* Stories Limit Warning Dialog */}
      <StoriesLimitWarningDialog
        open={storiesLimitDialog.open}
        onOpenChange={(open) => setStoriesLimitDialog({ ...storiesLimitDialog, open })}
        onConfirm={confirmCreateStory}
        onCancel={() => setStoriesLimitDialog({ ...storiesLimitDialog, open: false })}
        storiesBeingFrozen={storiesLimitDialog.storiesBeingFrozen}
        maxStories={storiesLimitDialog.maxStories}
        currentPlan={planState.plan ?? ''}
      />

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleConfirmDeleteStory}
        onCancel={() => setDeleteDialogOpen(false)}
        item={itemToDelete}
        isLive={itemToDelete?.isLive}
        liveLinkUrl={itemToDelete?.liveLinkUrl}
        liveLinkUnknown={itemToDelete?.liveLinkUnknown}
      />

    </PageFrame>
  );
}
